/* eslint-disable @stylistic/max-len */
import React, {type RefObject} from 'react';

/**
 * The `CalendarProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface CalendarProps {
    /**
     * A string representing the CSS class to be applied to the CalendarIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default '#0050FF'
     */
    color1?: string;
    /**
     * A number or string representing the height of the CalendarIcon element.
     *
     * @default 20
     */
    height?: number | string;
    /**
     * The `ref` property represents a reference to the SVG element, allowing for direct manipulation or access to the DOM node.
     */
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Calendar'
     */
    testId?: string;
    /**
     * A number or string representing the height of the CalendarIcon element.
     *
     * @default 20
     */
    width?: number | string;
}

/**
 * The `Calendar` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `CalendarProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the CalendarIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the CalendarIcon element.
 * @param props.ref       A reference to the SVG element, allowing for direct manipulation or access to the DOM node.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the CalendarIcon element.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const CalendarComponent = <Calendar className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const Calendar = ({
    className = '',
    color1 = '#0050FF',
    height = 20,
    ref,
    testId = 'Calendar',
    width = 20
}: CalendarProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 20 20"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <path d="M13.33 1.04c.35 0 .63.28.63.63V2.7h1.87a2.3 2.3 0 0 1 2.3 2.3v11.67a2.3 2.3 0 0 1-2.3 2.29H4.17a2.3 2.3 0 0 1-2.3-2.3V5a2.3 2.3 0 0 1 2.3-2.3h1.87V1.68a.62.62 0 1 1 1.25 0V2.7h5.42V1.67c0-.35.28-.63.62-.63M3.13 8.96v7.7c0 .58.46 1.05 1.04 1.05h11.66c.58 0 1.04-.47 1.05-1.04V8.96zm1.04-5c-.58 0-1.04.46-1.04 1.04v2.7h13.75V5c0-.58-.47-1.04-1.05-1.04h-1.87V5a.62.62 0 1 1-1.25 0V3.96H7.29V5a.63.63 0 0 1-1.25 0V3.96z" fill={color1} />
    </svg>
);

Calendar.displayName = 'Calendar';